import React, { useMemo, useState } from 'react'
import { com, kotlin } from '@eidu/web'
import { Stack } from '@common/ui'
import EntityTypeField from './EntityTypeField'
import PathField, { RenderablePath } from './PathField'
import getEntityPathLabel, { transformEntityPath } from './EntityPathUtils'
import EntityType = com.eidu.entity.type.EntityType
import EntityTypeId = com.eidu.entity.type.EntityTypeId
import KtList = kotlin.collections.KtList
import findPathsBetweenTypes = com.eidu.entity.path.findPathsBetweenTypes
import EntityPath = com.eidu.entity.path.EntityPath

export type EntityPathPermissionProps = {
  fromEntityTypeId: EntityTypeId | undefined
  preselectedPath?: EntityPath
  entityTypes: readonly EntityType[]
  onChange: (path: EntityPath | undefined) => void
  lockedToTypeId?: EntityTypeId
}

const EntityPathPermissionContent: React.FC<EntityPathPermissionProps> = ({
  fromEntityTypeId,
  preselectedPath,
  entityTypes,
  onChange,
  lockedToTypeId,
}) => {
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType>(
    lockedToTypeId
      ? entityTypes.find((type) => type.id.equals(lockedToTypeId)) ?? entityTypes[0]
      : preselectedPath
        ? entityTypes.find((type) => type.id.equals(preselectedPath.toType)) ?? entityTypes[0]
        : entityTypes[0]
  )
  const onChangeHandler = (value: EntityType) => {
    setSelectedEntityType(value)
  }
  const paths = useMemo(
    () =>
      fromEntityTypeId
        ? findPathsBetweenTypes(
            KtList.fromJsArray(entityTypes),
            fromEntityTypeId,
            selectedEntityType.id
          ).asJsReadonlyArrayView()
        : [],
    [entityTypes, fromEntityTypeId, selectedEntityType.id]
  )

  const renderablePaths: RenderablePath[] = useMemo(
    () =>
      paths
        .slice()
        .sort((a, b) => a.edges.asJsReadonlyArrayView().length - b.edges.asJsReadonlyArrayView().length)
        .map((path) => ({
          entityPath: path,
          entityPathLabel: getEntityPathLabel(entityTypes, path),
          entityPathNodes: transformEntityPath(entityTypes, path),
        })),
    [paths, entityTypes]
  )

  return (
    <Stack gap="xxs" p={lockedToTypeId ? 4 : 'xs'}>
      {!lockedToTypeId && (
        <EntityTypeField
          label="Target"
          entityTypeId={selectedEntityType.id}
          types={entityTypes}
          onChange={onChangeHandler}
        />
      )}
      <PathField paths={renderablePaths} selectedPath={preselectedPath} onChange={onChange} />
    </Stack>
  )
}

export default EntityPathPermissionContent
